import React, { useRef, useState, useEffect, memo } from "react";
import { useDispatch } from "react-redux";
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Grid, Button, CircularProgress, Select, MenuItem, TextField } from "@mui/material";
import { status, surface } from "../../../../theme/muiTheme";
import { DeviceParamaters } from "../../../actions/apiActions";
import { getAllParametersForDevice } from "../../../ducks/devicesParametersDuck";
import { useSelectedDevicesParameters, useSystemConfig } from "../../../hooks/useReduxSelectors";
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';


const ParameterConfigurationModal = memo(({ show, onHide, systemId, displayName, hwId, solutionType }) => {

  const [formState, setFormState] = useState([]);
  const [oldValues, setOldValues] = useState([]);
  const [selectedParameter, setSelectedParameter] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const holdButtonDownTimer = useRef(null);
  const dispatch = useDispatch();
  const selectedDevicesParameters = useSelectedDevicesParameters();
  const config = useSystemConfig();
  const isSensor = solutionType === "lora" ? true : false;

  const modalOpened = () => {
    setFormState([]);
    setOldValues([]);
    dispatch(getAllParametersForDevice({ hwId, isSensor, systemId }));
  };

  const selectInputField = (selectedParameterFromParameterState) => {
    setSelectedParameter(selectedParameterFromParameterState);

    // set value to virtual keyboard in edge environment
    if (systemId === "edge") {
      setShowKeyboard(true);
      let value = selectedParameterFromParameterState.value.toString();
      setInputValueVK(value);
    }
  };

  const onChangeHandler = (e, index, name) => {
    let inputValue = e.target.value;
    if (!isNaN(inputValue)) {
      inputValue = turnCommasToDots(inputValue);
    }
    let updatedParameter = ({ ...selectedParameter, value: inputValue, updated: "yes" });
    const updatedState = formState.map((parameter) => // this generates updated list by mapping trough current state and updating correct object from it
      parameter.parameterName === name ? updatedParameter : parameter //if paramaterState parameter name equals to selected parameter's name, update object, otherwise use original object
    );
    setFormState(updatedState);

    // set value to virtual keyboard in edge environment
    if (systemId === "edge") {
      setInputValueVK(inputValue);
    }
  };

  // Numeric value incrementation/decrementation button function
  const increaseOrDecrease = (index, parameterName, operator, e) => {
    let step = 1; // how much to increase or decrease the current value per step
    let newValue = 0;
    let precision = 0; // how many decimals does the current input field have

    // calculate precision with parameters floatPrecision value
    if (formState[index].floatPrecision) {
      switch (formState[index].floatPrecision) {
        case 0: break;
        case 1: step = 0.1; precision = formState[index].floatPrecision; break;
        case 2: step = 0.01; precision = formState[index].floatPrecision; break;
        case 3: step = 0.001; precision = formState[index].floatPrecision; break;
        default: break;
      }
    }

    const calculateNewValue = () => {
      let newValue;
      if (operator === "+") {
        newValue = Number(formState[index].value) + step;
      } else {
        newValue = Number(formState[index].value) - step;
      }
      newValue = Number(newValue.toFixed(precision));
      return newValue;
    };

    const updateState = (newValue) => {
      let updatedParameter = ({ ...formState[index], value: newValue.toString(), updated: "yes" });
      const updatedState = formState.map((parameter) => // this generates updated list by mapping trough current state and updating correct object from it
        parameter.parameterName === parameterName ? updatedParameter : parameter //if paramaterState parameter name equals to selected parameter's name, update object, otherwise use original object
      );
      setFormState(updatedState);
    };

    newValue = calculateNewValue();  // increase value
    if (e.type === "click") {
      updateState(newValue);
    } else {
      const startTime = new Date(); // initialize when we started holding button down;
      holdButtonDownTimer.current = setInterval(() => { //when button is held down, fire this every 0.1s
        const now = new Date();
        const delta = ((now.getTime() - startTime.getTime()) / 1000);
        let extraMultiplier = 0.7;
        if (delta > 8) { extraMultiplier = delta * 2.5; }
        else if (delta > 3) { extraMultiplier = delta * 1.2; }

        // increase value again with delta and extraMultiplier
        if (operator === "+") {
          newValue = newValue + (step * delta * extraMultiplier);
        } else {
          newValue = newValue - (step * delta * extraMultiplier);
        }
        newValue = Number(newValue.toFixed(precision));
        updateState(newValue);
      }, 100);
    }

    setSelectedParameter(formState[index]);

    // set value Vk
    if (systemId === "edge") {
      setInputValueVK(newValue);
    }
  };

  function holdButtonDownTimerClearUp() {
    if (holdButtonDownTimer.current) {
      clearInterval(holdButtonDownTimer.current);
      holdButtonDownTimer.current = null;
    }
  }

  const saveParameters = async () => {
    setSaving(true);
    const state = formState;
    let parameterListContainingOnlyChangedParameters = [];
    let formattedState = [];

    //check which parameters were changed and push them on parameterListContainingOnlyChangedParameters
    state.map((changedParameter, index) => {
      if (changedParameter.value !== oldValues[index].value) {
        parameterListContainingOnlyChangedParameters.push(changedParameter);
      }
    });

    //format changed parameters to contain only necessary values, push them to formattedState list
    try {
      parameterListContainingOnlyChangedParameters.map((parameter, index) => {
        let obj = {};
        let valueSelected = true;
        if (valueSelected) {
          obj.value = Number(parameter.value);
          obj.valueString = isNaN(parameter.value) ? parameter.value : null;
        }
        obj.parameterName = parameter.parameterName;
        obj.hwId = hwId;
        obj.isSensor = isSensor;
        formattedState.push(obj);
      });
      //save paramaters one at a time
      await Promise.all(formattedState.map(async (parameter) => {
        const payload = {
          ...parameter,
          customerId: (config && config.CUSTOMER_ID) || "",
          deviceId: (config && config.DEVICE_ID) || "",
        };
        await DeviceParamaters.post(config.DATA_URL, payload);
      }));
      setSaving(false);
      setErrorMsg("");
      setFormState([]);
      onHide();
    } catch (e) {
      e.message ?
        setErrorMsg("Error. " + e.message)
        :
        setErrorMsg("Error. Save was not successful");
      setSaving(false);
    }
  };

  // use this funcion to turn commas (,) to dots (.) from a value
  const turnCommasToDots = (input) => {
    let formattedInput = input;
    try {
      formattedInput = input.toString().replace(/\,/g, ".");
    } catch {
      return input;
    }
    return formattedInput;
  };

  // set formState and oldValues
  useEffect(() => {
    if ((selectedDevicesParameters.length > 0) && (formState.length === 0)) {
      setFormState(selectedDevicesParameters);
      setOldValues(selectedDevicesParameters);
    }
  }, [selectedDevicesParameters]);

  // remove possible error messages when formstate changes meaning when user has given new inputs
  useEffect(() => {
    setErrorMsg("");
  }, [formState]);


  ///////////////////////////
  //Virtual keyboard stuff //

  // Virtual keyboard variables
  const [inputValueVK, setInputValueVK] = useState("");
  const [layout, setLayout] = useState("default");
  const [showKeyboard, setShowKeyboard] = useState(false);
  const keyboard = useRef();
  // const inputPattern = /^\d+$/;

  const onChangeVirtualKeyboard = (input) => {
    let inputValue = input;
    inputValue = turnCommasToDots(inputValue);
    setInputValueVK(inputValue);
    let updatedParameter = ({ ...selectedParameter, value: inputValue, updated: "yes" });
    const updatedState = formState.map((parameter) => // this generates updated list by mapping trough current state and updating correct object from it
      parameter.parameterName === selectedParameter.parameterName ? updatedParameter : parameter //if paramaterState parameter name equals to selected parameter's name, update object, otherwise use original object
    );
    setFormState(updatedState);
  };

  const handleShift = () => {
    const newLayoutName = layout === "default" ? "shift" : "default";
    setLayout(newLayoutName);
  };

  const onKeyPress = button => {
    if (button === "{shift}" || button === "{lock}") handleShift();
  };

  useEffect(() => {
    if ((systemId === "edge") && (showKeyboard)) {
      let value = inputValueVK.toString();
      keyboard.current.setInput(value);
    }
  }, [inputValueVK]);

  //Virtual keyboard stuff ends //
  ////////////////////////////////


  const parameterConfigurationContent = () => {
    if (formState.length > 0) {
      return (
        <form>
          <div id="parameterConfigurationModal">
            {formState.map((parameter, index) => {
              return (
                parameter.type === "dropdown" ?
                  <Box key={index}>
                    <Grid container style={{ margin: "10px 0px" }}>
                      <Grid size={{ md: 12, lg: 5 }} sx={{ display: "flex", alignItems: "center", fontWeight: 600 }}>
                        {parameter.displayName}
                      </Grid>
                      <Grid size={{ md: 12, lg: 3 }} style={{ display: "flex" }}>
                        {parameter.updated &&
                          <Box component="span" sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                            {` Old Value: `}
                            {oldValues.length > 0 && oldValues.map((parameterOldValue, index) => parameterOldValue.parameterName === parameter.parameterName && parameterOldValue.value)}
                          </Box>
                        }
                      </Grid>
                      <Grid>
                        <Select
                          sx={parameter.updated === "yes" ? { bgcolor: status.updatedHighlight } : undefined}
                          value={parameter?.valueString ? parameter.valueString : parameter.value}
                          onChange={(e) => onChangeHandler({ target: { value: e.target.value } }, index, parameter.parameterName)}
                          onFocus={() => setSelectedParameter(formState[index])}
                          size="small"
                          fullWidth
                        >
                          {parameter.dropValues.map((dropValue, dvIndex) => (
                            <MenuItem key={dvIndex} value={dropValue}>{dropValue}</MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </Grid>
                  </Box>
                  :
                  <Box key={index}>
                    <Grid container style={{ margin: "10px 0px" }}>
                      <Grid size={{ md: 12, lg: 5 }} sx={{ display: "flex", alignItems: "center", fontWeight: 600 }}>
                        {parameter.displayName}
                      </Grid>
                      <Grid size={{ md: 12, lg: 3 }} style={{ display: "flex" }}>
                        {parameter.updated &&
                          <Box component="span" sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                            {` Old Value: `}
                            {oldValues.length > 0 && oldValues.map((parameterOldValue, index) => parameterOldValue.parameterName === parameter.parameterName && parameterOldValue.value)}
                          </Box>
                        }
                      </Grid>
                      <Grid>
                        <Box sx={{ display: "flex", flexDirection: "row", ...(parameter.updated === "yes" ? { bgcolor: status.updatedHighlight } : {}) }}>
                          <Button
                            onClick={(e) => increaseOrDecrease(index, parameter.parameterName, "-", e)}
                            onMouseDown={(e) => increaseOrDecrease(index, parameter.parameterName, "-", e)}
                            onMouseUp={holdButtonDownTimerClearUp}
                            onMouseLeave={holdButtonDownTimerClearUp}
                            onTouchStart={(e) => increaseOrDecrease(index, parameter.parameterName, "-", e)}
                            onTouchEnd={holdButtonDownTimerClearUp}
                            onTouchCancel={holdButtonDownTimerClearUp}
                          > -
                          </Button >
                          <TextField
                            sx={parameter.updated === "yes" ? { "& .MuiInputBase-input": { bgcolor: status.updatedHighlight } } : undefined}
                            type="text"
                            value={parameter.value && parameter.value}
                            onChange={(e) => onChangeHandler(e, index, parameter.parameterName)}
                            onFocus={() => selectInputField(formState[index])}
                            size="small"
                          />
                          <Button
                            onClick={(e) => increaseOrDecrease(index, parameter.parameterName, "+", e)}
                            onMouseDown={(e) => increaseOrDecrease(index, parameter.parameterName, "+", e)}
                            onMouseUp={holdButtonDownTimerClearUp}
                            onMouseLeave={holdButtonDownTimerClearUp}
                            onTouchStart={(e) => increaseOrDecrease(index, parameter.parameterName, "+", e)}
                            onTouchEnd={holdButtonDownTimerClearUp}
                            onTouchCancel={holdButtonDownTimerClearUp}
                          > +
                          </Button >
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
              );
            })}
          </div>
        </form>
      );
    } else {
      return (
        <Box style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
          <CircularProgress />
        </Box>
      );
    }
  };

  return (
    <Dialog
      open={show}
      onClose={onHide}
      maxWidth="xl"
      fullWidth
      id="parameterConfigurationModal"
      TransitionProps={{ onEnter: modalOpened }}
    >
      <DialogTitle>
        <span>{`${displayName} `}</span>
        <span>parameter configuration</span>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ maxHeight: "50vh", overflow: "auto" }}>
          <Box sx={{ borderTop: `2px solid ${surface.panel}`, px: "80px" }}>
            {parameterConfigurationContent()}
          </Box>
        </Box>
        {systemId === "edge" && showKeyboard &&
          <Keyboard
            keyboardRef={r => (keyboard.current = r)}
            layoutName={layout}
            onChange={onChangeVirtualKeyboard}
            onKeyPress={onKeyPress}
            theme={"hg-theme-default hg-layout-default custom-keyboard-style"}
          />
        }
      </DialogContent>
      {saving === true &&
        <CircularProgress style={{ display: "flex", alignSelf: "center", color: "white" }} />
      }
      <p style={{ fontSize: "2rem", alignSelf: "center", color: "red" }}>{errorMsg}</p>
      <DialogActions>
        <Button size="large" onClick={saveParameters} color="success" variant="contained">
          Save
        </Button>
        <Button size="large" onClick={onHide} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default ParameterConfigurationModal;